/** Chapter 9, platforms: one job each (15). */
import { chapterSpec } from "../deck/deckModel";
import {
  Eyebrow, Foot, Glyph, Headline, Key, Slide, SourceLine, V,
  countWord, countryName, platformName, slideText,
  type TemplateContext, NO_FIGURE
} from "./kit";

export function PlatformsSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const platforms = doc.platforms!;
  const t = slideText(ctx);
  const indexes = ctx.slide.template.id === "platforms" ? ctx.slide.template.platforms : [];
  const reach = doc.audience?.behaviour?.platforms_by_country ?? [];
  const markets = (doc.brief?.markets ?? []).map((m) => m.country);
  const volume = doc.content?.volume?.per_platform ?? [];
  const first = ctx.slide.continuation === 0 || ctx.slide.continuation == null;
  const n = platforms.list.length;
  const fallback = `${n > 1 ? `${countWord(n).replace(/^./, (c) => c.toUpperCase())} platforms` : "One platform"},\n*one job each.*`;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback={fallback} />
        <div className="sv-platforms">
          {indexes.map((i) => {
            const p = platforms.list[i];
            const who = markets
              .map((c) => {
                const share = reach.find((r) => r.platform === p.platform && r.country === c)?.reach_share;
                return share == null ? null : `${share}% in ${countryName(c)}`;
              })
              .filter((s): s is string => !!s)
              .join(" · ");
            const formats = [
              ...p.formats.map((f) => (f.share_pct != null ? `${f.name} ${f.share_pct}%` : f.name)),
              ...(p.formats_note ? [p.formats_note] : []),
            ].join(" · ");
            const posts = volume.find((v) => v.platform === p.platform)?.posts_per_month;
            return (
              <div key={p.platform} className="sv-platform" data-lead={p.is_primary || undefined}>
                <p className="sv-platform-top">
                  <Glyph platform={p.platform} size={22} />
                  <b>{platformName(p.platform)}</b>
                  {p.is_primary ? <span className="sv-platform-pill">Lead</span> : null}
                </p>
                <V as="p" className="sv-platform-job">{p.job}</V>
                {who ? <div className="sv-platform-line"><Key>Who is there</Key><V as="p">{who}</V></div> : null}
                {p.language ? <div className="sv-platform-line"><Key>Language</Key><V as="p">{p.language}</V></div> : null}
                {formats ? <div className="sv-platform-line"><Key>Formats</Key><V as="p">{formats}</V></div> : null}
                <span className="sv-push" />
                <div className="sv-platform-bottom">
                  {posts != null ? <V as="b">{posts}</V> : <b>{NO_FIGURE}</b>}
                  <p>
                    <span>posts a month</span>
                    {p.judged_by_label ? <span>judged on <V>{p.judged_by_label}</V></span> : null}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        {first && (platforms.account_setup || platforms.excluded.length) ? (
          <div className="sv-platforms-notes">
            {platforms.account_setup ? (
              <div className="sv-platforms-note">
                <Key>Account set-up</Key>
                <V as="p">{platforms.account_setup}</V>
              </div>
            ) : null}
            {platforms.excluded.length ? (
              <div className="sv-platforms-note">
                <Key>{platforms.excluded.map((x) => <Glyph key={x.platform} platform={x.platform} size={14} />)}Left out on purpose</Key>
                <p>{platforms.excluded.map((x, i) => <span key={x.platform}>{i ? " " : null}{platformName(x.platform)}: <V>{x.reason}</V></span>)}</p>
              </div>
            ) : null}
          </div>
        ) : null}
        <span className="sv-push" />
        <SourceLine className="sv-source-plain">
          {reach.length ? <>Reach of internet users: <V>{(reach[0].source?.label ?? "reference table per country").replace(/^./, (c) => c.toLowerCase())}</V>. </> : null}
          Posts a month: chapter {chapterSpec("content")?.number}. The number each platform is judged on: chapter {chapterSpec("measuring")?.number}.
        </SourceLine>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}
